import { getScoreLevel } from '@thiememeulenhoff/types';
import {
  TmElement,
  focusStyles,
  rem,
  radius,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tagName } from '../../helpers';

const tag = tagName('rating');

const starSize = 24;

const ratingStyles = [
  css`
    :host {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
    }

    :host([disabled]) {
      pointer-events: none;
    }

    :where(.stars) {
      align-items: center;
      display: flex;
      margin-inline-end: ${spacing(150)};
    }

    :where(.star) {
      position: relative;
    }

    :where(.score) {
      margin-inline-start: ${spacing(100)};
    }

    /* This element acts as the 'container' box, but is transparent */
    :where(input) {
      appearance: none;
      background-color: transparent;
      block-size: ${rem(starSize)};
      border-radius: ${radius('small')};
      cursor: pointer;
      display: block;
      inline-size: ${rem(starSize)};
      margin: 0;
      padding: 0;
      position: relative;
    }

    :where(input:focus-visible) {
      ${focusStyles};
      outline-offset: 0;
      z-index: 1;
    }

    :where(a-icon) {
      font-size: ${rem(starSize)};
      inset-block-start: 0;
      inset-inline-start: 0;
      position: absolute;
    }

    :where(a-icon:not(.selected)) {
      color: ${token('foreground-subtle')};
    }

    :where(a-icon[name='star']) {
      color: ${token('foreground-selected')};
    }
  `,
];

@customElement(tag)
export class RatingComponent extends TmElement {
  static override styles = ratingStyles;

  @property({ type: Number, reflect: true })
  @state()
  max = 5;

  @property({ type: Number, reflect: true, attribute: 'value' })
  @state()
  currentValue?: number;

  @state()
  hoverValue?: number;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: String })
  placeholder = 'Geef jezelf een score';

  @property({ type: Array })
  messages = ['Slecht', 'Onvoldoende', 'Voldoende', 'Goed', 'Perfect'];

  get value() {
    return this.hoverValue || this.currentValue || 0;
  }

  get score() {
    if (!this.value) return undefined;
    return this.value * 20;
  }

  get scoreLevel() {
    if (!this.score) return undefined;
    return getScoreLevel(this.score);
  }

  override render() {
    return [this.#starsTemplate(), this.#textTemplate()];
  }

  #starsTemplate() {
    const stars = Array.from({ length: this.max }).map((_, index) => {
      const starScore = index + 1;
      const selected = starScore <= this.value;
      return html`
        <div class="star">
          <a-icon name=${selected ? 'star' : 'star-empty'}></a-icon>
          <input
            data-test="radio"
            type="radio"
            name="rating"
            value=${starScore}
            @mouseover=${() => this.#setHoverScore(starScore)}
            @mouseleave=${() => this.#setHoverScore(undefined)}
            @change=${() => this.#select(starScore)}
            .disabled=${this.disabled}
            aria-label="${`star count: ${index}`}"
          />
        </div>
      `;
    });
    return html`<div class="stars ${this.scoreLevel}">${stars}</div>`;
  }

  #textTemplate() {
    if (this.currentValue) return nothing;
    return html`<div class="text">${this.placeholder}</div>`;
  }

  #setHoverScore(score?: number) {
    this.hoverValue = score;
  }

  #select(value: number) {
    this.currentValue = value;
    this.dispatchLocalEvent({
      eventName: 'selected',
      detail: this.value,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-rating': RatingComponent;
  }
}
